import React, {useContext, useState, Fragment} from 'react';
import moment from "moment";
import { useQueryClient } from 'react-query';
import {Button, Grid, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Checkbox, FormControlLabel, useTheme} from '@material-ui/core';

import { CurrencyValue, ConvertedNumber, Alert } from "../../../controls";

import { useFuelFees } from "../../../services/fuel";
import { useAirports } from "../../../services/airports";

const ConfirmFuelDialog = (_props) => {
  const queryClient = useQueryClient();
  const theme = useTheme();

  const props = {
    fuelRateUuid: 'dummy',
    ..._props
  };
  const {
    fuelRateUuid, 
    airportCode, 
    airportVariant, 
    plattsPrice, 
    dollarPlattsPrice, 
    overrideCalculation, 
    rate,
    exchangeRateType,
    priceUom,
    currencyCode,
    priceBasis,
    supplierCode,
    companyName,
    validFrom,
    validTo
  } = props.record||{};

  const fuelFees = useFuelFees({ queryClient, extraKeys: [fuelRateUuid], useCache: true });
  const airport = useAirports({ queryClient, extraKeys: [airportCode], useCache: true });

  const rows = fuelFees.data||[];

  const [totals, setTotals] = useState(
    {'DIFFERENTIAL': 0, 'FIXED': 0, 'FEE': 0, completed: false}
  );
  const [validationState, setValidationState] = useState({
    error: true,
    errorMessage: "checking",
    warning: false,
    warningMessage: "",
    checked: false,
    field: null
  });

  const handleCancel = () => {
    if (props.onChange)
      props.onChange({state: 'cancel'});
  };
  
  const handleConfirm = () => {
    if (props.onChange)
      props.onChange({state: 'confirm'});
  };
  
  if (!totals.completed && fuelFees.isSuccess && validationState.errorMessage === 'checking') {
    let error = false;
    let errorMessage = "";
    let warning = false;
    let warningMessage = "";

    const changedTotals = {...totals};
    fuelFees.data.forEach(fee => {
      changedTotals[fee.groupType] += fee.dollarAmount;
      if ((fee.dollarAmount||0).toFixed(3) < 1 && !warning) {
        warning = true; warningMessage = "One or more fees has a value less than 1 cent, please check this is correct";
      }
      if ((fee.dollarAmount||0).toFixed(3) === (0).toFixed(3) && !error) {
        error = true; errorMessage = "One or more fees has a value equal to zero";
      }
    });
    if (!error) {
      if ((changedTotals.DIFFERENTIAL||0).toFixed(3) !== (props.record.totalDiffs||0).toFixed(3)) {
        error = true; errorMessage = "There is a difference in the Differential Fee totals";
      } else if ((changedTotals.FEE||0).toFixed(3) !== (props.record.totalFees||0).toFixed(3)) {
        error = true; errorMessage = "There is a difference in the Fee totals";
      } else if ((changedTotals.FIXED||0).toFixed(3) !== (props.record.totalFixedFees||0).toFixed(3)) {
        error = true; errorMessage = "There is a difference in the Fixed Fee totals";
      }
    }

    setValidationState({error, errorMessage, warning, warningMessage});
    setTotals({...changedTotals, completed: true});
  }
  
  const handleConversionError = (errorMessage, errorCode = null) => {
    if (!validationState.error) {
      setValidationState({...validationState, error: true, errorMessage});
    }
  }

  if (props.open){
    return (
      <div id="ConfirmFuelDialog">
        <Dialog open={props.open}
                onClose={handleCancel}
                aria-labelledby="form-dialog-title"
                disableBackdropClick={true}
                fullWidth={true}
                maxWidth="md"
        >
          <DialogTitle id="form-dialog-title">
            <div style={{clear: "both", backgroundColor: theme.palette.primary.main, padding:"10px"}}>
              Confirm Fuel Rate and Fees for {airportCode} {airport.data && airport.data.length === 1?` - ${airport.data[0].airportName}`:''}
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>Please verify and confirm the rates below are accurate.</strong><br/>
              (Please note these rates are used in customer charges directly, <strong>SMALL discrepancies</strong> can result in <strong>LARGE losses</strong>! Please check carefully!) <br/><br/>
            </DialogContentText>
            <Fragment>
              <div>
                <Grid container spacing="1" nowrap="true" key={"header"}>
                  <Grid item xs={2} nowrap="true"><div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}>Airport:</div></Grid>
                  <Grid item xs={4} nowrap="true" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{airportCode} {airport.data && airport.data.length === 1?` - ${airport.data[0].airportName}`:''}</Grid>
                  <Grid item xs={2} nowrap="true"><div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}>Supplier:</div></Grid>
                  <Grid item xs={4} nowrap="true" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{supplierCode} - {companyName}</Grid>
                  { priceBasis ? <Grid item xs={2} nowrap="true"><div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}>Price Basis:</div></Grid> : <></> }
                  { priceBasis ? <Grid item xs={4} nowrap="true" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{priceBasis}</Grid> : <></> }
                  <Grid item xs={2} nowrap="true"><div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}>Validity Period:</div></Grid>
                  <Grid item xs={4} nowrap="true" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{moment.utc(validFrom).format('DD MMM YYYY')} to {moment.utc(validTo).format('DD MMM YYYY')}</Grid>
                </Grid>
              </div>
              <div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}></div>
              <div style={{ fontWeight: "bolder", fontSize: 15, height: 20}}>Rate</div>
              <div style={{ marginLeft: 10 }}>
                <Grid container spacing="1" nowrap="true" key="rate">
                  <Grid item xs={6} nowrap="true" style={{ minWidth: 160 }}><div style={{ display: "inline-block", minWidth: 45 }}>Fuel uplift cost</div></Grid>
                  <Grid item xs={3} nowrap="true" style={{ minWidth: 80, textAlign: "right" }}>
                    <CurrencyValue
                      decimalScale={2}
                      fixedDecimalScale={true}
                      currencyCode={currencyCode}
                      value={plattsPrice}
                    /><span style={{display: "inline-block"}}>&nbsp;{priceUom ? `/ ${priceUom}` : ""}</span>
                  </Grid>
                  <Grid item xs={3} nowrap="true" key="rate" style={{ minWidth: 80, textAlign: "right" }}>
                    <CurrencyValue
                      decimalScale={3}
                      fixedDecimalScale={true}
                      currencyCode={'USDC'}
                      value={dollarPlattsPrice}
                    /><span style={{display: "inline-block"}}>&nbsp;/ USG</span>
                  </Grid>
                </Grid>
              </div>
              <div style={{ fontWeight: "bolder", fontSize: 15, height: 20, marginTop: 4}}>Fees and Charges</div>
              <div style={{ marginLeft: 10 }}>
              {
                rows.map((row, i) => {
                  if (row.groupType === "DIFFERENTIAL") {
                    return (
                      <Grid container spacing="1" nowrap="true" key={i}>
                        <Grid item xs={6} nowrap="true" style={{ minWidth: 160 }}><div style={{ display: "inline-block", minWidth: 45 }}>(Differential)</div></Grid>
                        <Grid item xs={3} nowrap="true" key={i} style={{ minWidth: 80, textAlign: "right" }}>
                          <CurrencyValue
                            decimalScale={3}
                            fixedDecimalScale={true}
                            currencyCode={row.currencyCode}
                            value={row.amount}
                          /><span style={{display: "inline-block"}}>&nbsp;{row.priceUom ? `/ ${row.priceUom}` : ""}</span>
                        </Grid>
                        <Grid item xs={3} nowrap="true" key={i} style={{ minWidth: 80, textAlign: "right" }}>
                        <span style={{display: "inline-block"}}><ConvertedNumber
                            displayType="text"
                            value={row.amount}
                            convertedValue={row.dollarAmount}
                            dontConvert={true}
                            showError={false}
                            highlightError={!row.overrideCalculation}
                            onError={handleConversionError}
                            decimalScale={3}
                            rateProviderCode={exchangeRateType}
                            fromCurrencyCode={row.currencyCode}
                            toCurrencyCode="USDC"
                            rateDate={validFrom}
                            prefix="$c "
                            fixedDecimalScale={true}
                            uom={row.priceUom}
                            style={{ width:"100%", textAlign: "right"}}
                          /></span><div style={{display: "inline-block"}}>&nbsp;/ USG</div>
                        </Grid>
                      </Grid>
                    )
                  } else if (row.groupType === "FIXED") {
                    return (
                      <Grid container spacing="1" nowrap="true" key={i}>
                        <Grid item xs={6} nowrap="true" style={{ minWidth: 160 }}><div style={{ display: "inline-block", minWidth: 45 }}>(Fixed Fee)</div><div style={{ display: "inline-block" }}>{row.name?row.name:'Other Unknown'}</div></Grid>
                        <Grid item xs={3} nowrap="true" style={{ minWidth: 80, textAlign: "right" }}>
                          <CurrencyValue
                            decimalScale={2}
                            fixedDecimalScale={true}
                            currencyCode={row.currencyCode}
                            value={row.amount}
                          />
                        </Grid>
                        <Grid item xs={3} nowrap="true" key={i} style={{ minWidth: 80, textAlign: "right" }}>
                        <span style={{display: "inline-block"}}><ConvertedNumber
                            displayType="text"
                            value={row.amount}
                            convertedValue={row.dollarAmount}
                            dontConvert={true}
                            showError={false}
                            highlightError={!row.overrideCalculation}
                            onError={handleConversionError}
                            decimalScale={3}
                            rateProviderCode={exchangeRateType}
                            fromCurrencyCode={row.currencyCode}
                            toCurrencyCode="USDC"
                            rateDate={validFrom}
                            prefix="$c "
                            fixedDecimalScale={true}
                            uom={row.priceUom}
                            style={{ width:"100%", textAlign: "right"}}
                          /></span><div style={{display: "inline-block"}}>&nbsp;/ USG</div>
                        </Grid>
                      </Grid>
                    )
                  } else {
                    return (
                      <Grid container spacing="1" nowrap="true" key={i}>
                        <Grid item xs={6} nowrap="true" style={{ minWidth: 160 }}><div style={{ display: "inline-block", minWidth: 45 }}>(Fees / Taxes)</div><div style={{ display: "inline-block" }}>{row.name?row.name:'Other Unknown'}</div></Grid>
                        <Grid item xs={3} nowrap="true" style={{ minWidth: 80, textAlign: "right" }}>
                          <CurrencyValue
                            decimalScale={3}
                            fixedDecimalScale={true}
                            currencyCode={row.currencyCode}
                            value={row.amount}
                          /><div style={{width: 90, minWidth:90, display: "inline"}}>&nbsp;{row.priceUom ? `/ ${row.priceUom}` : ""}</div>
                        </Grid>
                        <Grid item xs={3} nowrap="true" key={i} style={{ minWidth: 80, textAlign: "right" }}>
                          <span style={{display: "inline-block"}}><ConvertedNumber
                            displayType="text"
                            value={row.amount}
                            convertedValue={row.dollarAmount}
                            dontConvert={true}
                            showError={false}
                            highlightError={!row.overrideCalculation}
                            onError={handleConversionError}
                            decimalScale={3}
                            rateProviderCode={exchangeRateType}
                            fromCurrencyCode={row.currencyCode}
                            toCurrencyCode="USDC"
                            rateDate={validFrom}
                            prefix="$c "
                            fixedDecimalScale={true}
                            uom={row.priceUom}
                            style={{ width:"100%", textAlign: "right"}}
                          /></span><div style={{display: "inline-block"}}>&nbsp;/ USG</div> 
                        </Grid>
                      </Grid>
                    )
                  }
                })
              }
              </div>
            </Fragment >
            <Alert
              key="confirmfuelerror"
              open={validationState.error}
              severity='error'
              message={validationState.errorMessage}
            />
            <Alert
              key="confirmfuelwarning"
              open={validationState.warning && !validationState.error}
              severity='warning'
              message={validationState.warningMessage}
            />
          </DialogContent>
          <DialogActions>
            <div style={{display: "flex", marginLeft: 10, flex: 'auto'}}>
              <FormControlLabel control={<Checkbox onChange={(event)=>{setValidationState({...validationState, checked: event.target.checked})}}/>} label="I confirm that I have checked the rate and all fees are correct" />
            </div>  
            <Button onClick={handleCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={validationState.error || !validationState.checked} color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  } else {
    return (<></>);
  }
}

export default ConfirmFuelDialog;